import React from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import FontAwesomeIcon from 'src/components/Icon';
import Button from 'src/components/Button';
import {
  IKeyPickupSummary,
  IUserAssociatedProperty,
  JuniperEventType,
} from '../../types';
import './key-pickup-property-card.scss';

interface KeyPickupCardActionsProps {
  property: IUserAssociatedProperty;
  hasKeyPickupLink: boolean;
  hasInvitable: boolean;
  onManage: (property: IUserAssociatedProperty) => void;
}

const KeyPickupCardActions = ({
  property,
  hasKeyPickupLink,
  hasInvitable,
  onManage,
}: KeyPickupCardActionsProps) => {
  // Deep-link straight to this property's key-pickup booking links in the Booking links tab.
  const createLinkHref: string = `${PATHS.CALENDAR_CALCOM_BOOKINGS}/${property.propertyId}?kind=${JuniperEventType.KeyPickup}`;

  // Fail fast: no booking link means scheduling can't work at all, so we point the user at
  // where to create one instead of opening the invite dialog.
  if (!hasKeyPickupLink) {
    return (
      <div className="card-needs-link">
        <span className="card-needs-link-note">
          Needs a key-pickup booking link that belongs to you before homeowners
          can be invited.
        </span>
        <Link to={createLinkHref} className="card-create-link-cta">
          Create a key-pickup booking link →
        </Link>
      </div>
    );
  }

  if (hasInvitable) {
    return (
      <div className="card-actions">
        <Button
          color="teal"
          size="small"
          icon={<FontAwesomeIcon glyph="key" weight="solid" size="small" />}
          label="Invite units"
          onClick={() => onManage(property)}
        />
      </div>
    );
  }

  return (
    <p className="card-no-invitable">
      No units ready to invite yet — add valid key pickups to this
      property&apos;s units first.
    </p>
  );
};

interface KeyPickupPropertyCardProps {
  property: IUserAssociatedProperty;
  summary: IKeyPickupSummary | null;
  hasKeyPickupLink: boolean;
  onManage: (property: IUserAssociatedProperty) => void;
}

const KeyPickupPropertyCard = ({
  property,
  summary,
  hasKeyPickupLink,
  onManage,
}: KeyPickupPropertyCardProps) => {
  const totalCount: number = summary?.totalCount ?? 0;
  const invitableCount: number = summary?.invitableCount ?? 0;
  const hasInvitable: boolean = invitableCount > 0;

  return (
    <div className="key-pickup-property-card">
      <h5 className="property-name">{property.name}</h5>

      <div className="card-body">
        {totalCount === 0 ? (
          <p className="card-summary">No units in this property yet.</p>
        ) : (
          <p className="card-summary">
            <strong>
              {invitableCount} of {totalCount}
            </strong>{' '}
            unit{totalCount === 1 ? '' : 's'} ready to invite
          </p>
        )}

        <KeyPickupCardActions
          property={property}
          hasKeyPickupLink={hasKeyPickupLink}
          hasInvitable={hasInvitable}
          onManage={onManage}
        />
      </div>
    </div>
  );
};

export default KeyPickupPropertyCard;
